import { createStore } from "zustand/vanilla";
import { useStore } from "zustand";
import type { AppRuntime } from "../../runtime/contracts";
import { useAppRuntime } from "../../providers/AppRuntimeProvider";
import type { ChatListFilter } from "./helpers";

export interface AgentModuleStoreState {
  selectedEpisodeId: string | undefined;
  searchQuery: string;
  /** "Needs reply / All" list pane filter — see ChatListFilter. */
  listFilter: ChatListFilter;
  actions: {
    setSelectedEpisodeId: (id: string | undefined) => void;
    setSearchQuery: (query: string) => void;
    setListFilter: (filter: ChatListFilter) => void;
  };
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/explicit-module-boundary-types
export function createAgentModuleStore(_runtime: AppRuntime) {
  return createStore<AgentModuleStoreState>((set) => ({
    selectedEpisodeId: undefined,
    searchQuery: "",
    // @tested-by: tst_fe_chats_filter_004
    // @invariant: default filter is "all" — current Chats behavior unchanged
    listFilter: "all",
    actions: {
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      setSelectedEpisodeId: (id) => { set({ selectedEpisodeId: id }); },
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      setSearchQuery: (query) => { set({ searchQuery: query }); },
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      setListFilter: (filter) => { set({ listFilter: filter }); },
    },
  }));
}

export type AgentModuleStore = ReturnType<typeof createAgentModuleStore>;
export function useAgentModuleStore(): AgentModuleStoreState;
export function useAgentModuleStore<T>(selector: (state: AgentModuleStoreState) => T): T;
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/explicit-module-boundary-types
export function useAgentModuleStore<T>(selector?: (state: AgentModuleStoreState) => T) {
  const runtime = useAppRuntime();
  const store = runtime.stores.get<AgentModuleStore>("episodes");
  if (!store) throw new Error("Agent module store not initialized");
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  return useStore(store, selector ?? ((s) => s as unknown as T));
}
